import * as React from "react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetClose,
} from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { formatDate, formatPhoneNumber, formatToPKR } from "@/lib/helper";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { walletTransactionStatus } from "@/lib/constants/global";
import { WalletRequest, WalletRequestStatus } from "@/types/wallet";
import { Check, Mail, Phone, X } from "lucide-react";
import { Card, CardHeader } from "@/components/ui/card";
import { useGetMemberByIdQuery } from "@/services/memberApi";
import { useApprovedRequestMutation, useRejectedRequestMutation } from "@/services/walletApi";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { useToast } from "@/components/ui/use-toast";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ErrorType } from "@/types";
import warning from "@/assets/warning.svg";
import { mintToken } from "@/components/admin/pos/sell";
const { VITE_VIEW_S3_URL } = import.meta.env;

// Add this helper function
const getFullReceiptUrl = (receiptUrl: string) => {
  // If it's already a full URL, return as is
  if (receiptUrl.startsWith('http://') || receiptUrl.startsWith('https://')) {
    return receiptUrl;
  }
  
  // If the receipt path already starts with "images/", remove it to avoid duplication
  const cleanedPath = receiptUrl.startsWith('images/') 
    ? receiptUrl.substring(7) 
    : receiptUrl;
  
  // Combine the base URL with the cleaned path
  return `${VITE_VIEW_S3_URL}/${cleanedPath}`;
};

interface WalletRequestDetailProps {
  isOpen: boolean;
  onClose: () => void;
  data: WalletRequest | null;
  onApprove?: (id: number) => void;
  onReject?: (id: number) => void;
  refetch?: () => void; // Add this prop for refreshing data
}

const WalletRequestDetail = ({
  isOpen,
  onClose,
  data,
  onApprove,
  onReject,
  refetch,
}: WalletRequestDetailProps) => {
  const { toast } = useToast();
  const orgId =
    useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;
  const jwtToken = localStorage.getItem("userToken") || "";

  // Add state for dialogs
  const [isApproveDialogOpen, setIsApproveDialogOpen] = React.useState(false);
  const [isRejectDialogOpen, setIsRejectDialogOpen] = React.useState(false);
  const [rejectionReason, setRejectionReason] = React.useState("");
  const [isProcessingApproval, setIsProcessingApproval] = React.useState(false);

  // API mutations
  const [approveRequest, { isLoading: isApproving }] =
    useApprovedRequestMutation();
  const [rejectRequest, { isLoading: isRejecting }] =
    useRejectedRequestMutation();

  // Add key prop to force re-query when member_id changes
  const memberId = data?.member_id || 0;

  const { data: memberInfo, isLoading: memberInfoLoading } =
    useGetMemberByIdQuery(
      { org_id: orgId, id: Number(memberId) },
      {
        skip: !memberId || isOpen === false,
        // Force refetch when member_id changes
        refetchOnMountOrArgChange: true,
      }
    );

  // Handle approve transaction
  const handleApprove = async () => {
    if (!data) return;

    try {
      setIsProcessingApproval(true);
      
      // Validate wallet_id exists and is not empty
      if (!data.wallet_id || data.wallet_id.trim() === "") {
        toast({
          variant: "destructive",
          title: "Error",
          description:
            "Wallet address is missing. Cannot proceed with approval.",
        });
        setIsProcessingApproval(false);
        return;
      }

      // Validate if it's a valid Ethereum address format
      const ethereumAddressRegex = /^0x[a-fA-F0-9]{40}$/;
      if (!ethereumAddressRegex.test(data.wallet_id)) {
        toast({
          variant: "destructive",
          title: "Error",
          description:
            "Invalid Ethereum wallet address format. Please verify the address.",
        });
        setIsProcessingApproval(false);
        return;
      }

      // First call the mintToken API
      const mintResponse = await mintToken({
        user_id: data.member_id,
        transaction_id: 0,
        invoice_id: 0, // Using request ID as invoice_id
        jwt_token: jwtToken,
        org_id: orgId,
        address: data.wallet_id,
        amount: data.request_amount,
        payment_method: "Bank Transfer",
        product_name: "Bank Transfer",
        product_description: "Amount transfered from wallet request",
      });

      // Check if token minting was successful
      if (mintResponse.success) {
        // If minting succeeded, proceed with approving the request
        await approveRequest({
          id: data.id,
          status: WalletRequestStatus.APPROVED,
          org_id: orgId,
        }).unwrap();

        toast({
          variant: "success",
          title: "Transaction Approved",
          description: "The wallet has been topped up successfully.",
        });

        if (refetch) refetch();
        setIsApproveDialogOpen(false);
        onClose();
      } else {
        // If minting failed
        toast({
          variant: "destructive",
          title: "Error",
          description: "Service wallet top up has some issues.",
        });
      }
    } catch (error) {
      console.error("Error approving transaction", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Service wallet top up has some issues.",
      });
    } finally {
      setIsProcessingApproval(false);
    }
  };

  // Handle reject transaction
  const handleReject = async () => {
    if (!data) return;

    if (!rejectionReason.trim()) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Please provide a reason for rejection.",
      });
      return;
    }

    try {
      await rejectRequest({
        id: data.id,
        status: WalletRequestStatus.REJECTED,
        rejection_reason: rejectionReason,
        org_id: orgId,
      }).unwrap();

      toast({
        variant: "success",
        title: "Transaction Rejected",
        description: "The wallet transaction has been rejected.",
      });

      if (refetch) refetch();
      setIsRejectDialogOpen(false);
      setRejectionReason("");
      onClose();
    } catch (error) {
      console.error("Error rejecting transaction", error);
      handleApiError(error);
    }
  };

  // Error handler
  const handleApiError = (error: any) => {
    if (error && typeof error === "object" && "data" in error) {
      const typedError = error as ErrorType;
      toast({
        variant: "destructive",
        title: "Error",
        description: `${typedError.data?.detail || typedError.data?.message || "Something went wrong."}`,
      });
    } else {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Something went wrong. Please try again.",
      });
    }
  };

  // Handle text input to ensure only single spaces
  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    // Replace multiple spaces with a single space
    const formattedText = value.replace(/\s+/g, " ");

    // Apply character limit (150 chars)
    if (formattedText.length <= 150) {
      setRejectionReason(formattedText);
    }
  };

  if (!data) return null;

  // Get status badge styling
  const getStatusBadge = (status: string) => {
    const statusItem = walletTransactionStatus.find(
      (item) => item.value === status.toLowerCase()
    );
    return {
      label: statusItem?.label || status,
      bgColor: statusItem?.color || "bg-gray-200",
    };
  };

  const statusBadge = getStatusBadge(data.status);

  return (
    <>
      <Sheet open={isOpen} onOpenChange={onClose}>
        <SheetContent
          hideCloseButton
          className="w-full !max-w-full sm:!max-w-[750px] py-0 custom-scrollbar"
        >
          <SheetHeader className="flex flex-row justify-between border-b items-center">
            <div className="flex flex-wrap items-center justify-between gap-3 w-full p-4">
              <div className="flex items-center gap-2">
                <SheetClose asChild>
                  <Button size="icon" variant="ghost">
                    <X className="h-4 w-4" />
                  </Button>
                </SheetClose>
                <SheetTitle className="text-base sm:text-xl">
                  Load Request Details
                </SheetTitle>
              </div>
              {/* Action buttons for pending requests */}
              {data.status.toLowerCase() === "pending" && (
                <div className="flex items-center gap-2 sm:gap-3 shrink-0">
                  <Button
                    variant="outline"
                    onClick={() => setIsRejectDialogOpen(true)}
                    className="border-red-300 text-red-600 hover:bg-red-50 text-xs sm:text-sm h-8 sm:h-9"
                  >
                    Reject
                  </Button>
                  <Button
                    onClick={() => setIsApproveDialogOpen(true)}
                    className="bg-primary text-white text-xs sm:text-sm h-8 sm:h-9"
                  >
                    Approve
                  </Button>
                </div>
              )}
            </div>
          </SheetHeader>

          <ScrollArea className="h-[calc(100vh-120px)] pr-4 mt-6">
            <div className="space-y-6">
              <Card className="w-full px-3 bg-white rounded-lg overflow-hidden">
                {/* Member info section */}
                <CardHeader className="p-3 border-b mb-3">
                  <div className="flex items-center justify-between">
                    <div>Request Details</div>
                    <div className="text-sm ">
                      Request Amount: ({formatToPKR(data.request_amount)})
                    </div>
                  </div>
                </CardHeader>
                <div className="flex items-start gap-4 pb-4">
                  <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center text-gray-500">
                    {data.member_name?.charAt(0).toUpperCase() || "U"}
                  </div>
                  <div className="flex-1">
                    <div className="flex flex-row items-center justify-between">
                      <div>
                        <h3 className="font-semibold capitalize">
                          {data.member_name}
                        </h3>
                      </div>
                      <div className="flex items-center">
                        <Badge className={`${statusBadge.bgColor} text-white`}>
                          {statusBadge.label}
                        </Badge>
                      </div>
                    </div>

                    <div className="flex flex-row items-center gap-2 mt-1">
                      <span className="text-sm text-gray-500 flex flex-row items-center gap-1">
                        <Mail className="h-4 w-4" />{" "}
                        {memberInfo?.email || "N/A"}
                      </span>
                      <span className="text-sm text-gray-500"> | </span>
                      <span className="text-sm text-gray-500 flex flex-row items-center gap-1">
                        {" "}
                        <Phone className="h-4 w-4" />
                        {formatPhoneNumber(memberInfo?.mobile_number || "N/A")}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground break-all pt-2">
                      Wallet Id/Address : {data.wallet_id}
                    </p>
                  </div>
                </div>
              </Card>
              <Card className="w-full px-3 p-2 bg-white rounded-lg overflow-hidden">
                <CardHeader className="p-3 border-b mb-3">
                  <div className="flex items-center justify-between">
                    <div>Attached Reciept</div>
                  </div>
                </CardHeader>
                {/* Request details section */}
                <div className="space-y-4">
                  {/* Receipt section if needed */}
                  {data.receipt_url && (
                    <div className="space-y-2">
                      <div className="border rounded-md p-3">
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <i className="fa-solid fa-paperclip"></i>
                          <a
                            href={getFullReceiptUrl(data.receipt_url)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-blue-600 hover:underline"
                          >
                            Receipt{" "}
                          </a>
                          (
                          {data.created_at &&
                            `Uploaded on ${formatDate(data.created_at)}`}
                          )
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </Card>

              {/* Rejection reason if exists */}
              {data.rejection_reason && (
                <div className="space-y-2">
                  <h3 className="font-medium text-base">Rejection Reason</h3>
                  <p className="text-sm p-3 bg-red-50 border border-red-100 rounded-md break-all ">
                    {data.rejection_reason}
                  </p>
                </div>
              )}
            </div>
          </ScrollArea>
        </SheetContent>
      </Sheet>

      {/* Approve Confirmation Dialog */}
      <Dialog open={isApproveDialogOpen} onOpenChange={setIsApproveDialogOpen}>
        <DialogContent onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle>Confirm Approval</DialogTitle>
            <DialogDescription>
              <div className="flex flex-col items-center justify-center gap-4 mt-4">
                <Check className="h-16 w-16 text-green-500" />
                <p className="text-center">
                  Are you sure you want to approve this wallet transaction?
                </p>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2 sm:justify-end">
            <Button
              variant="outline"
              onClick={() => setIsApproveDialogOpen(false)}
              disabled={isApproving}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              className="bg-green-600 hover:bg-green-700"
              onClick={handleApprove}
              disabled={isProcessingApproval}
            >
              {isProcessingApproval ? "Processing..." : "Approve"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Confirmation Dialog */}
      <AlertDialog
        open={isRejectDialogOpen}
        onOpenChange={(open) => {
          // Only allow closing with the cancel button, not when clicking outside
          if (open === false && rejectionReason.trim() === "") {
            return;
          }
          setIsRejectDialogOpen(open);
        }}
      >
        <AlertDialogContent onClick={(e) => e.stopPropagation()}>
          <AlertDialogHeader>
            <AlertDialogDescription>
              <div className="flex flex-col items-center justify-center gap-4">
                <img src={warning} alt="warning" className="w-18 h-18" />
                <AlertDialogTitle className="text-xl font-semibold w-80 text-center">
                  Reject this wallet transaction?
                </AlertDialogTitle>
              </div>
              <div className="my-4">
                <Label htmlFor="reason" className="text-sm font-medium">
                  Rejection Reason
                </Label>
                <Textarea
                  id="reason"
                  className="mt-2"
                  placeholder="Please provide a reason for rejection"
                  value={rejectionReason}
                  onChange={handleTextChange}
                  rows={4}
                  maxLength={150}
                />
                <div className="text-xs text-gray-500 mt-1 text-right">
                  {rejectionReason.length}/150 characters
                </div>
              </div>
              <div className="w-full flex justify-between items-center gap-3 mt-4">
                <AlertDialogCancel
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsRejectDialogOpen(false);
                    setRejectionReason("");
                  }}
                  disabled={isRejecting}
                  className="w-full border border-primary font-semibold"
                >
                  <i className="fa fa-xmark text-base px-1"></i>
                  Cancel
                </AlertDialogCancel>
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!rejectionReason.trim()) {
                      toast({
                        variant: "destructive",
                        title: "Error",
                        description: "Please provide a reason for rejection.",
                      });
                      return;
                    }
                    handleReject();
                  }}
                  disabled={isRejecting}
                  className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold"
                >
                  {isRejecting ? "Rejecting..." : "Reject"}
                </Button>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default WalletRequestDetail;